import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  notification,
  Descriptions,
  List,
  Dropdown,
  Menu,
  Popconfirm,
  Tag,
} from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchCurrentUser, updateUser } from "../services/authAPI";
import {
  deleteProject,
  archiveProject,
  requestProjectExport,
  fetchProjectExportDownload,
} from "../services/projectsAPI";
import {
  createCheckoutSession,
  requestCancelSubscription,
} from "../services/paymentsAPI";
import { useAuth } from "../hooks/useAuth";
import {
  convertRole,
  formatPhoneNumber,
  handlePhoneNumberKeyDown,
} from "../utils/textUtils";
import queryClient from "../queryClient";
import UpdateProjectModal from "../components/UpdateProjectModal";
import CreatePremiumProjectModal from "../components/CreatePremiumProjectModal";

const apiErrorMessage = (error) => {
  const detail = error.response?.data?.detail;
  return (typeof detail === "string" ? detail : detail?.message) || error.message;
};

const UserSettings = () => {
  const { setCurrentUser } = useAuth();
  const [form] = Form.useForm();
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isCreatePremiumModalVisible, setIsCreatePremiumModalVisible] =
    useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const exportProjectId = searchParams.get("export_project");
  const handledExportRef = useRef(null);

  useEffect(() => {
    if (!exportProjectId || handledExportRef.current === exportProjectId) {
      return;
    }
    handledExportRef.current = exportProjectId;
    fetchProjectExportDownload(exportProjectId)
      .then((data) => {
        setSearchParams({}, { replace: true });
        notification.success({
          message: "Project export download started",
          description: data?.file_name,
        });
        window.location.href = data.url;
      })
      .catch((error) => {
        if (error?.response?.status === 401) {
          return;
        }
        setSearchParams({}, { replace: true });
        notification.error({
          message: "Failed to download project export",
          description: apiErrorMessage(error),
        });
      });
  }, [exportProjectId, setSearchParams]);

  const { data: userInfo, isLoading } = useQuery({
    queryKey: ["currentUser"],
    queryFn: fetchCurrentUser,
  });

  const canCreateProject =
    Array.isArray(userInfo?.projects) &&
    userInfo.projects.some(
      (p) => p.role === "owner" || p.role === "project_manager"
    );

  useEffect(() => {
    if (userInfo) {
      form.setFieldsValue({
        first_name: userInfo.first_name,
        last_name: userInfo.last_name,
        company_name: userInfo.company_name,
        phone_number: formatPhoneNumber(userInfo.phone_number),
        notes: userInfo.notes,
      });
    }
  }, [userInfo]);

  const mutation = useMutation({
    mutationFn: updateUser,
    onSuccess: (data) => {
      setCurrentUser(data);
      notification.success({ message: "User info updated successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update user info",
        description: error.message,
      });
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: deleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries(["currentUser"]);
      notification.success({ message: "Project deleted successfully" });
      updateCurrentUser();
    },
    onError: (error) => {
      notification.error({
        message: "Failed to delete project",
        description: error.message,
      });
    },
  });

  const archiveProjectMutation = useMutation({
    mutationFn: ({ projectId, archived }) =>
      archiveProject(projectId, archived),
    onSuccess: async (_data, variables) => {
      notification.success({
        message: variables.archived ? "Project archived" : "Project unarchived",
      });
      const user = await fetchCurrentUser();
      queryClient.setQueryData(["currentUser"], user);
      setCurrentUser(user);
      localStorage.setItem("user", JSON.stringify(user));
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update archive status",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const exportProjectMutation = useMutation({
    mutationFn: requestProjectExport,
    onSuccess: (data) => {
      notification.success({
        message: "Project export requested",
        description: data?.message,
      });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to request project export",
        description: apiErrorMessage(error),
      });
    },
  });

  const downgradeMutation = useMutation({
    mutationFn: requestCancelSubscription,
    onSuccess: () => {
      notification.success({
        message:
          "Cancellation request submitted. We will respond within 24 hours.",
      });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to submit cancellation request",
        description: error.message,
      });
    },
  });

  const createCheckoutMutation = useMutation({
    mutationFn: createCheckoutSession,
    onSuccess: (response) => {
      window.location.href = response.checkout_url;
    },
    onError: (error) => {
      notification.error({
        message:
          "There was an error processing your request. Please try again later.",
        description: error.message,
      });
    },
  });

  const updateCurrentUser = async () => {
    const user = await fetchCurrentUser();
    setCurrentUser(user);
    localStorage.setItem("user", JSON.stringify(user));
    window.location.reload();
  };

  const handleUpdateUser = (values) => {
    mutation.mutate(values);
  };

  const handleUpdateClick = (project) => {
    setSelectedProject(project);
    setIsUpdateModalVisible(true);
  };

  const handleUpgradeClick = async (project, plan) => {
    createCheckoutMutation.mutate({
      plan: plan,
      projectId: project.project_id,
    });
  };

  const projectActions = (project) => {
    const menuItems = [];

    if (project.role === "owner") {
      menuItems.push(
        <Menu.Item key="1" onClick={() => handleUpdateClick(project)}>
          Update
        </Menu.Item>
      );

      if (project.project_tier === 0) {
        menuItems.push(
          <Menu.Item
            key="2"
            onClick={() => handleUpgradeClick(project, "professional")}
            disabled={createCheckoutMutation.isPending}
          >
            {createCheckoutMutation.isPending
              ? "Processing..."
              : "Upgrade to Professional Tier"}
          </Menu.Item>
        );
        menuItems.push(
          <Menu.Item
            key="3"
            onClick={() => handleUpgradeClick(project, "enterprise")}
            disabled={createCheckoutMutation.isPending}
          >
            {createCheckoutMutation.isPending
              ? "Processing..."
              : "Upgrade to Enterprise Tier"}
          </Menu.Item>
        );
      }

      menuItems.push(archiveMenuItem(project));

      if (project.settings?.allow_project_export) {
        menuItems.push(
          <Menu.Item
            key="export"
            disabled={exportProjectMutation.isPending}
            onClick={() => exportProjectMutation.mutate(project.project_id)}
          >
            Export Project Data
          </Menu.Item>
        );
      }

      menuItems.push(
        <Menu.Item key="4">
          <Popconfirm
            title="Warning: This action cannot be undone! Are you sure you want to permanently delete this project and all its associated information?"
            onConfirm={() => deleteProjectMutation.mutate(project.project_id)}
            okText="Delete"
            cancelText="Cancel"
          >
            Delete Project
          </Popconfirm>
        </Menu.Item>
      );
    }

    if (project.role === "project_manager") {
      menuItems.push(
        <Menu.Item key="view" onClick={() => handleUpdateClick(project)}>
          View
        </Menu.Item>
      );
      menuItems.push(archiveMenuItem(project));
    }

    return <Menu>{menuItems}</Menu>;
  };

  const archiveMenuItem = (project) => (
    <Menu.Item
      key="archive"
      disabled={archiveProjectMutation.isPending}
      onClick={() =>
        archiveProjectMutation.mutate({
          projectId: project.project_id,
          archived: !project.archived,
        })
      }
    >
      {project.archived ? "Unarchive" : "Archive"}
    </Menu.Item>
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!userInfo) {
    return <div>Unable to load user settings. Check that the API is running.</div>;
  }

  return (
    <div className="user-settings">
      <h2>User Settings</h2>
      <Descriptions bordered column={1} style={{ marginBottom: 24 }}>
        <Descriptions.Item label="Email">
          <div className="flex items-center justify-between gap-2 w-full">
            <span>{userInfo.email}</span>
            <Popconfirm
              title="Are you sure you want to cancel your subscription? You'll lose access to premium features."
              onConfirm={() => downgradeMutation.mutate()}
              okText="Cancel"
              cancelText="Keep"
              okButtonProps={{ loading: downgradeMutation.isPending }}
              variant="text"
            >
              <Button
                color="danger"
                size="small"
                loading={downgradeMutation.isPending}
              >
                {downgradeMutation.isPending
                  ? "Cancelling..."
                  : "Cancel Subscription"}
              </Button>
            </Popconfirm>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label="Phone Number">
          {userInfo.phone_number}
        </Descriptions.Item>
        <Descriptions.Item label="Notes">{userInfo.notes}</Descriptions.Item>
      </Descriptions>
      <div className="flex justify-between items-center mb-4">
        <h3>Projects</h3>
        {canCreateProject && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreatePremiumModalVisible(true)}
            className="mr-6"
          >
            Add Project
          </Button>
        )}
      </div>
      <List
        bordered
        dataSource={userInfo.projects}
        renderItem={(project) => (
          <List.Item
            className={project.archived ? "opacity-60" : ""}
            actions={
              project.role === "owner"
                ? [
                    <Dropdown
                      overlay={projectActions(project)}
                      trigger={["click"]}
                    >
                      <Button icon={<EditOutlined />} />
                    </Dropdown>,
                  ]
                : project.role === "project_manager"
                ? [
                    <Dropdown
                      overlay={projectActions(project)}
                      trigger={["click"]}
                    >
                      <Button
                        icon={<EditOutlined />}
                        title="Project Settings"
                      />
                    </Dropdown>,
                  ]
                : null
            }
          >
            <List.Item.Meta
              title={
                <div className="flex items-center space-x-2">
                  <span>{project.project_name}</span>
                  <Tag color={project.project_tier === 0 ? "blue" : "gold"}>
                    {project.project_tier === 0 ? "Free" : "Premium"}
                  </Tag>
                  {project.archived && <Tag>Archived</Tag>}
                </div>
              }
              description={`Role: ${convertRole(project.role)}, Vendor: ${
                project.vendor_name || "N/A"
              }`}
            />
          </List.Item>
        )}
      />
      <Form
        form={form}
        onFinish={handleUpdateUser}
        layout="vertical"
        style={{ marginTop: 24 }}
      >
        <Form.Item
          label="First Name"
          name="first_name"
          rules={[{ required: true, message: "Please input your first name!" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Last Name"
          name="last_name"
          rules={[{ required: true, message: "Please input your last name!" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Company Name"
          name="company_name"
          rules={[
            { required: true, message: "Please input your company name!" },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Phone Number" name="phone_number">
          <Input
            onKeyDown={handlePhoneNumberKeyDown}
            onChange={(e) => {
              const formatted = formatPhoneNumber(e.target.value);
              form.setFieldsValue({ phone_number: formatted });
            }}
          />
        </Form.Item>
        <Form.Item label="Notes" name="notes">
          <Input.TextArea rows={4} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            Update
          </Button>
        </Form.Item>
      </Form>

      <UpdateProjectModal
        visible={isUpdateModalVisible}
        onClose={() => setIsUpdateModalVisible(false)}
        project={selectedProject}
        readOnly={selectedProject?.role !== "owner"}
      />
      <CreatePremiumProjectModal
        visible={isCreatePremiumModalVisible}
        onClose={() => setIsCreatePremiumModalVisible(false)}
      />
    </div>
  );
};

export default UserSettings;
